import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Sheet } from '@/app/_components/Sheet';
import { ELButton, ELLoader } from '@davincihealthcare/elty-design-system-react';
import Modal from '@/app/_components/modal';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { RecapSection } from '@/app/_components/widgets/listing/cardV2/RecapSection';
import { useAvailabilitySlotsOld } from '@/app/_components/widgets/listing/_hooks/useAvailabilitySlotsV2';
import { SlotAvailability } from '@/app/_components/widgets/listing/types';
import { TimeButton } from '@/app/_components/widgets/listing/cardV2/TimeButton';
import { getDayTitle } from '@/app/_components/widgets/listing/_utils/time';
import { useIsTablet } from '@/app/_hooks/useIsScreen';
import { formatDate } from '@/utils/dates';
import { InlineBanner } from '@/app/_components/AlertBanner';
import { trackAddToCart } from '@/utils/trackingMeta-pixelV2';
import { getCookieByName } from '@/utils/cookies';
import { usePatient } from '@/app/_hooks/usePatient';

interface AvailabilityModalProps {
  specialityId: string;
  doctorId: string;
  workgroupId: string;
  onClose: () => void;
  onSubmit: (selectedSlot: SlotAvailability) => void | Promise<void>;
  initialSelectedDay: number | 'OTHER' | null;
  firstDayExpected?: number | null;
}

const scrollToElement = (id: string) => {
  setTimeout(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 50);
};

export const AvailabilityModalV2: FC<AvailabilityModalProps> = ({
  specialityId,
  doctorId,
  workgroupId,
  onClose,
  onSubmit,
  initialSelectedDay,
  firstDayExpected,
}) => {
  const { patient } = usePatient();
  const [selectedSlot, setSelectedSlot] = useState<SlotAvailability | null>(null);
  const [hasBeenInitialized, setHasBeenInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { groupedDays, sortedDates, isInitialLoading, fetchNextPage, isLoadingMore, hasMoreData } = useAvailabilitySlotsOld({
    specialityId,
    doctorId,
    workgroupId,
    firstAvailabilityDate: firstDayExpected,
  });

  const isTabletAndSmaller = useIsTablet();

  /*
    Wait for the first load to complete
    Scroll to the selected day or the first available slot if initialSelectedDay is an existing day
    Scroll to the "show more times" button if initialSelectedDay is OTHER and there are slots available, fetch also additional slots
  */
  useEffect(() => {
    // If the component has been initialized or there are no slots, do nothing
    if (hasBeenInitialized || sortedDates.length === 0 || isInitialLoading) return;
    if (initialSelectedDay && groupedDays[initialSelectedDay]) {
      const firstSlot = groupedDays[initialSelectedDay][0];
      if (firstSlot) {
        setSelectedSlot(firstSlot);
        scrollToElement(`day-${initialSelectedDay}`);
      }
    }

    if (!initialSelectedDay) {
      const firstDay = sortedDates[0];
      if (firstDay && groupedDays[firstDay]) {
        setSelectedSlot(groupedDays[firstDay][0]);
      }
    }

    setHasBeenInitialized(true);
  }, [initialSelectedDay, groupedDays, sortedDates.length, hasBeenInitialized, isInitialLoading, sortedDates, fetchNextPage]);

  const handleLoadMore = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  const handleSubmit = useCallback(async () => {
    if (!selectedSlot) return;
    setIsSubmitting(true);
    await trackAddToCart({
      fbc: getCookieByName('_fbc') || '',
      fbp: getCookieByName('_fbp') || '',
      userEmail: patient?.emails && patient?.emails.length > 0 ? patient?.emails[0] : '',
      price: selectedSlot.price || 0,
      service: selectedSlot.id,
    });
    await onSubmit(selectedSlot);
    setIsSubmitting(false);
  }, [selectedSlot, patient?.emails, onSubmit]);

  const continueButton = (
    <ELButton
      label="Continua"
      variant="filled"
      color="primary"
      trailingIcon={<ArrowRightIcon />}
      size="large"
      disabled={!selectedSlot || isSubmitting}
      onClick={handleSubmit}
    />
  );

  const warningMessage = useMemo(() => {
    const firstDay = sortedDates[0];

    if (!firstDayExpected || firstDayExpected === firstDay || isInitialLoading) return;

    if (!firstDay || firstDay > firstDayExpected)
      return {
        type: 'laterDay',
        message: (
          <div>
            Siamo spiacenti, la prestazione non è più disponibile in data&nbsp;
            <b>
              {formatDate({
                date: new Date(firstDayExpected),
                localeOptions: { day: '2-digit', month: 'long' },
              }).replace(/(\b[a-z])/g, match => match.toUpperCase())}
            </b>
            . Le disponibilità orarie possono variare in base alle prenotazioni fatte da altri utenti.
          </div>
        ),
      };

    return {
      type: 'earlierDay',
      message: (
        <div>
          <b>Ottime notizie!</b> C&apos;è una disponibilità anticipata per la prestazione che hai richiesto. Le disponibilità orarie possono
          variare in base alle prenotazioni fatte da altri utenti.
        </div>
      ),
    };
  }, [firstDayExpected, sortedDates, isInitialLoading]);

  const dayList = isInitialLoading ? (
    <div className="flex h-full w-full items-center justify-center">
      <ELLoader />
    </div>
  ) : (
    <div className="flex flex-col gap-6">
      {warningMessage && (
        <InlineBanner
          title="Disponibilità orarie"
          description={warningMessage.message}
          type={warningMessage.type === 'earlierDay' ? 'info' : 'warning'}
        />
      )}
      {sortedDates.map(timestamp => {
        const slots = groupedDays[timestamp];

        return (
          <div key={timestamp} id={`day-${timestamp}`} className="flex flex-col gap-3">
            <h3 className="font-semibold capitalize">{getDayTitle(timestamp)}</h3>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(70px,1fr))] gap-2">
              {slots.map(slot => (
                <TimeButton
                  key={slot.start}
                  dataCy={`slot-button`}
                  dataTimestamp={slot.start}
                  title={formatDate({
                    date: new Date(slot.start),
                    localeOptions: { hour: '2-digit', minute: '2-digit' },
                  })}
                  isSelected={selectedSlot?.start === slot.start}
                  onClick={() => setSelectedSlot(slot)}
                />
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex w-full flex-col items-center gap-2 md:flex-row">
        <ELButton
          id="show-more-times"
          label={hasMoreData ? 'Mostra altri orari' : 'Nessun altro orario disponibile'}
          variant="text"
          disabled={isInitialLoading || isLoadingMore || !hasMoreData}
          color="primary"
          size="small"
          onClick={handleLoadMore}
        />
        {isLoadingMore && <ELLoader />}
      </div>
    </div>
  );

  if (isTabletAndSmaller) {
    return (
      <Modal isModalOpen={true} onClose={onClose} className="m-0 h-full max-h-full w-full max-w-full px-4 pt-5">
        <div className="relative flex h-full max-h-full w-full max-w-full flex-col gap-6">
          <div className="grid grid-cols-[1.5rem_1fr_1.5rem] items-center justify-end text-right">
            {/*Future back button*/}
            <div />
            <h2 className="text-center text-base font-semibold text-primary">Scelta orario</h2>
            <button aria-label="Chiudi" className="focus:outline-none" onClick={onClose}>
              <XMarkIcon className="w-full " />
            </button>
          </div>

          <RecapSection selectedSlot={selectedSlot} />

          <div className="scrolling-shadow flex-1 overflow-y-auto pb-28">{dayList}</div>

          <div className="fixed bottom-0 left-0 w-full px-4 [&>button]:w-full">
            <div className="h-6 w-full bg-gradient-to-t from-white via-white/90 to-transparent" />
            {continueButton}
            <div className="h-6 w-full bg-gradient-to-b from-white via-white/90 to-transparent" />
          </div>
        </div>
      </Modal>
    );
  }
  return (
    <Sheet isOpen={true} onClose={onClose}>
      <div className="relative flex h-full max-h-full flex-col gap-6 px-20 pb-5 pt-10">
        <h2 className="text-2xl font-semibold text-primary">Scelta orario</h2>
        <RecapSection selectedSlot={selectedSlot} />
        <div className="scrolling-shadow flex-1 overflow-y-auto">{dayList}</div>
        <div className="w-fit">{continueButton}</div>
      </div>
    </Sheet>
  );
};
